import { useQuery, useMutation } from '@tanstack/react-query'
import { useGroupsStore } from '@/store'
import { GroupsFilterParams, CreateGroupParams, UpdateGroupParams, PermissionsFilterParams } from '@/types/group'
import { BulkIdParams } from '@/types/general'
import { useBulkDeleteSuccessHandler } from '@/hooks/use-bulk-delete'

export const useGroups = (params: GroupsFilterParams = {}) => {
  const fetchGroups = useGroupsStore(state => state.fetchGroups)

  return useQuery({
    queryKey: ['groups', params],
    queryFn: () => fetchGroups(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000 // 10 minutes
  })
}

export const useGroup = (id: string) => {
  const getGroup = useGroupsStore(state => state.getGroup)

  return useQuery({
    queryKey: ['group', id],
    queryFn: () => getGroup(id),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!id
  })
}

export const usePermissions = (params?: PermissionsFilterParams, options?: { enabled?: boolean }) => {
  const fetchPermissions = useGroupsStore(state => state.fetchPermissions)

  return useQuery({
    queryKey: ['permissions', params],
    queryFn: () => fetchPermissions(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: options?.enabled ?? true
  })
}

export const useCreateGroup = () => {
  const createGroup = useGroupsStore(state => state.createGroup)

  return useMutation({
    mutationFn: (params: CreateGroupParams) => createGroup(params),
    onSuccess: () => {},
    throwOnError: false,
    meta: {
      errorMessage: false
    }
  })
}

export const useUpdateGroup = () => {
  const updateGroup = useGroupsStore(state => state.updateGroup)

  return useMutation({
    mutationFn: ({ id, params }: { id: string; params: UpdateGroupParams }) => updateGroup(id, params),
    onSuccess: () => {},
    throwOnError: false,
    meta: {
      errorMessage: false
    }
  })
}

export const useDeleteGroup = () => {
  const deleteGroup = useGroupsStore(state => state.deleteGroup)

  return useMutation({
    mutationFn: (id: string) => deleteGroup(id),
    onSuccess: () => {},
    throwOnError: false,
    meta: {
      errorMessage: false
    }
  })
}

export const useBulkDeleteGroups = () => {
  const bulkDeleteGroups = useGroupsStore(state => state.bulkDeleteGroups)
  const deselectAllGroups = useGroupsStore(state => state.deselectAllGroups)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (params: BulkIdParams) => bulkDeleteGroups(params),
    onSuccess: response => {
      deselectAllGroups()
      handleBulkDeleteSuccess(response)
    },
    throwOnError: false,
    meta: {
      errorMessage: false
    }
  })
}
